const MEDIA_HERO_ID_PREFIX = 'media-hero';
const MEDIA_CARD_SECTION_PREFIXES = ['media-cards-container', 'media-cards', 'media-card'];
const COVER_ID_PREFIXES = ['media-card-img', 'media-card-image'];
const CONTENT_ID_PREFIX = 'media-card-content';

/**
 * @param {Object} section
 * @returns {boolean}
 */
export const isMediaHeroSection = section =>
  section?.sectionType === 'hero' || (section?.sectionId || '').startsWith(MEDIA_HERO_ID_PREFIX);

const hasMediaCardBlock = section =>
  (section?.blocks || []).some(block => (block.blockId || '').startsWith('media-card'));

/**
 * Any non-hero CMS section on /p/media that represents a press card.
 *
 * @param {Object} section
 * @returns {boolean}
 */
export const isMediaCardSection = section => {
  if (!section || isMediaHeroSection(section) || section.sectionType === 'footer') {
    return false;
  }

  const sectionId = section.sectionId || '';
  return (
    MEDIA_CARD_SECTION_PREFIXES.some(prefix => sectionId.startsWith(prefix)) ||
    hasMediaCardBlock(section) ||
    section.sectionType === 'columns'
  );
};

const matchesCoverId = id => COVER_ID_PREFIXES.some(prefix => (id || '').startsWith(prefix));

const getFieldText = field => {
  if (!field) {
    return '';
  }
  if (typeof field.content === 'string') {
    return field.content;
  }
  if (typeof field === 'string') {
    return field;
  }
  return '';
};

/**
 * Parse a CMS published-date string such as "Published on 20th October, 2026".
 *
 * @param {string} text
 * @returns {number} epoch ms, or 0 when unparseable
 */
export const parseMediaCardPublishedAt = text => {
  if (!text) {
    return 0;
  }

  const cleaned = String(text)
    .replace(/<[^>]+>/g, ' ')
    .replace(/published on/gi, ' ')
    .replace(/\b(\d+)(st|nd|rd|th)\b/gi, '$1')
    .replace(/\s+/g, ' ')
    .trim();

  if (!cleaned) {
    return 0;
  }

  const timestamp = Date.parse(cleaned);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const toCard = (section, coverBlock, contentBlock, index) => ({
  id: `${section?.sectionId || 'media-card'}-${index}`,
  sectionId: section?.sectionId || null,
  cmsIndex: index,
  publishedAt: parseMediaCardPublishedAt(getFieldText(contentBlock?.text)),
  coverMedia: coverBlock?.media || null,
  logoMedia: contentBlock?.media || null,
  title: contentBlock?.title || null,
  text: contentBlock?.text || null,
  callToAction: contentBlock?.callToAction || null,
});

/**
 * Collect CMS media card sections into a flat array the mediaCards block can render.
 * Each CMS section becomes exactly one card.
 *
 * @param {Array<Object>} sections PageBuilder section configs
 * @returns {Array<Object>} cards
 */
export const collectMediaCards = (sections = []) =>
  sections
    .filter(isMediaCardSection)
    .map((section, index) => {
      const blocks = section.blocks || [];
      const coverBlock =
        blocks.find(block => matchesCoverId(block.blockId)) ||
        blocks.find(
          block => block.media && !block.title && !block.text && !block.callToAction
        ) ||
        null;
      const contentBlock =
        blocks.find(block => (block.blockId || '').startsWith(CONTENT_ID_PREFIX)) ||
        blocks.find(block => block !== coverBlock) ||
        null;

      return toCard(section, coverBlock, contentBlock, index);
    })
    .sort((a, b) => {
      const dateDiff = (b.publishedAt || 0) - (a.publishedAt || 0);
      if (dateDiff !== 0) {
        return dateDiff;
      }
      // Same or missing dates: keep the most recently added CMS section first.
      return (b.cmsIndex || 0) - (a.cmsIndex || 0);
    });
